import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Table from 'react-bootstrap/Table';
import { getDynamicFormAutomations } from "../../../../../../../services/api/dynamic-fields-api";
import { FluentButton } from "../../../../../../../components/FluentUI/Fluent-Button";
import FluentUiIcon from "../../../../../../../assets/fluent-ui-icon";
import { NIL as NIL_UUID } from "uuid";
import { ThenModal } from "./then-Modal";
import { WhenModal } from "./when-Modal";
import { DEFAULT_DYNAMIC_FORM_AUTOMATION_THEN_AVAILABILITY, DEFAULT_THEN_EVENT_STATE, DEFAULT_WHEN_EVENT_STATE, EmptyState } from "./automation-helpers";
import { PulseLoader } from "react-spinners";
import { scrollToBottomByClassName } from "../../../../../../../util/general-helpers";

const AutomationsGrid = ({ allForms, selectedForm, taskTypeId, taskStatusList, formStatuses, allUsers, formAutomationFromParent, SetFormAutomation, setSelectedForm }) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showThenModal, setShowThenModal] = useState({ show: false, isChained: false, selectedThenEvent: DEFAULT_THEN_EVENT_STATE, selectedThenAvailability: [DEFAULT_DYNAMIC_FORM_AUTOMATION_THEN_AVAILABILITY] });
    const [showWhenModal, setShowWhenModal] = useState({ show: false, isChained: false, selectedWhenEvent: DEFAULT_WHEN_EVENT_STATE });
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [fieldsInputs, setFieldsInputs] = useState([]);
    const [selectedWhenIndex, setSelectedWhenIndex] = useState(null);
    const [selectedThenIndex, setSelectedThenIndex] = useState(null);

    useEffect(() => {
        if (!taskTypeId || !selectedForm?.Id) return;
        setLoading(true);
        setError(null);
        let selectedFormAutomations = formAutomationFromParent?.filter(x => x?.FormId == selectedForm?.Id);
        if (selectedFormAutomations && selectedFormAutomations.length > 0) {
            setRows(selectedFormAutomations);
            setLoading(false);
        }
        else {
            (async () => {
                try {
                    const result = await getDynamicFormAutomations(taskTypeId, selectedForm?.Id);
                    const mapped = (Array.isArray(result) ? result : []).map(item => {
                        const base = item.DynamicFormAutomation || { Id: NIL_UUID, AutomationOrder: 0 };
                        return {
                            Id: base.Id,
                            AutomationOrder: base.AutomationOrder,
                            FormId: selectedForm?.Id,
                            TaskTypeId: taskTypeId,
                            When: item.DynamicFormAutomationWhen,
                            Then: item.DynamicFormAutomationThen
                        };
                    }).sort((a, b) => (a.AutomationOrder || 0) - (b.AutomationOrder || 0));
                    setRows(mapped);
                } catch (err) {
                    console.error("Failed to load dynamic form automations", err);
                    setError("Failed to load automations.");
                } finally {
                    setLoading(false);
                }
            })();
        }

    }, [taskTypeId, selectedForm]);

    useEffect(() => {
        let fields = [];
        selectedForm?.SubForms?.forEach(element => {
            element.Fields?.forEach(field => {
                fields.push(field);
            });
        });
        setFieldsInputs(fields);
    }, [selectedForm]);


    //#region Render
    const buildThenStructures = (thenArr = []) => {
        return (thenArr || []).map(t => {
            const ev = t.DynamicFormAutomationThen || {};
            return {
                id: ev.Id,
                parentId: ev.RelatedTo,
                condition: (ev.RelatedToCondition || "").toUpperCase(),
                eventType: ev.EventType,
                fieldName: ev.EventDynamicFieldName || "",
                sectionName: ev.EventDynamicSectionName || "",
                statusName: ev.EventTypeStatusName || "",
                value: ev.EventTypeValue,
                visible: ev.EventDynamicFieldVisible,
                editable: ev.EventDynamicFieldEditable,
                mandatory: ev.EventDynamicFieldMandatory,
                availability: (t.DynamicFormAutomationThenAvailability || []).map(a => ({
                    id: a.Id,
                    groupName: a.GroupName,
                    userName: a.UserName,
                    roleName: a.MemberPermissionName
                }))
            };
        });
    };

    const renderWhenCell = (row) => {
        if (!row.When || !row.When.length) {
            return <span onClick={() => setNewWhenEvent(row.AutomationOrder)} className="text-new-automation">Set an Event Type</span>;
        }

        const whenArr = row.When;

        return (
            <div>
                {whenArr.map((w, idx) => {
                    let description;

                    switch (w.EventType) {
                        case "Task Created":
                            description = <div className="whenThenEventDescription">When "<span className="semibold">Task is Created</span>"</div>;
                            break;

                        case "Task Status Changed To":
                            description = <div className="whenThenEventDescription">When Task Status is Changed To "<span className="semibold">{w.EventTypeStatusName}</span>"</div>;
                            break;

                        case "Form Status Changed To":
                            description = <div className="whenThenEventDescription">When Form "<span className="semibold">{w.EventFormName}</span>" status changes to <span className="semibold">{w.EventTypeStatusName}</span></div>;
                            break;

                        case "Form Submission":
                            description = <div className="whenThenEventDescription">When Form "<span className="semibold">{w.EventFormName}</span>" is "<span className="semibold">Submitted</span>"</div>;
                            break;

                        case "Field Input":
                            const triggerMap = {
                                "Save Form": "Form Save",
                                "Submit Form": "Form Submission",
                                "Live Input": "Live Input"
                            };
                            const triggerText = triggerMap[w.EventTypeTrigger] || w.EventTypeTrigger;
                            description = <div className="whenThenEventDescription">When the Form "<span className="semibold">{w.EventFormName}</span>" has the value "<span className="semibold">{w.EventDynamicFieldName}</span>" set to "<span className="semibold">{w.EventTypeValue}</span>" upon "<span className="semibold">{triggerText}</span>"</div>;
                            break;

                        default:
                            description = <div className="whenThenEventDescription">{w.EventType}</div>;
                    }

                    return (
                        <div key={idx} style={{ marginBottom: "8px" }}>
                            <div className={`${w.RelatedToCondition != null && w.RelatedToCondition !== "" ? "mt-2 mb-2" : ""}`}>
                                <strong>{w.RelatedToCondition}</strong>
                            </div>
                            <div>{description}</div>
                            <div className="mt-1">
                                <span className="editAutomation" style={{ cursor: "pointer" }} onClick={() => editWhenAutomation(row.AutomationOrder, w, idx)}>
                                    Edit
                                </span>
                                {(whenArr.length > 0 && idx == whenArr.length - 1)
                                    && <> {" · "}
                                        <span className="editAutomation" onClick={() => setNewWhenEvent(row.AutomationOrder, true)} style={{ cursor: "pointer" }}>
                                            Chain Event
                                        </span>
                                    </>}
                                {(whenArr.length == 1 || (whenArr.length > 1 && idx > 0)) && <span className="deleteAutomation" onClick={() => handleWhenDelete(row.AutomationOrder, idx)} style={{ cursor: "pointer" }}>{" · "}Delete</span>}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const formatThenEventType = (eventType, details) => {
        switch (eventType) {
            case "Change Form Status To":
                return (
                    <>
                        The Status for this Selected Form is Transitioned to "<span className="semibold">{details.statusName}</span>"
                    </>
                );
            case "Reset to Status":
                return (
                    <>
                        The Status for this Selected Form is Reset to "<span className="semibold">{details.statusName}</span>"
                    </>
                );

            case "Form Availability":
                return (
                    <>
                        This Selected Form is {details.value == "True" ? "Available" : "Unavailable"} to "<span className="semibold">{details.availability}</span>"
                    </>
                );

            case "Field Availability":
                return (
                    <>
                        "<span className="semibold">{details.fieldName}</span>" field is Available to "<span className="semibold">{details.availability}</span>" with Editable: "<span className="semibold">{details.editable ? "Yes" : "No"}</span>", Mandatory: "<span className="semibold">{details.mandatory ? "Yes" : "No"}</span>", Visible: "<span className="semibold">{details.visible ? "Yes" : "No"}</span>"
                    </>
                );
            case "Field Reset":
                return (
                    <>
                        "<span className="semibold">{details.fieldName}</span>" field will be reset
                    </>
                );
            case "Field Insert":
                return (
                    <>
                        "<span className="semibold">{details.fieldName}</span>" field{details.value ? <> value "<span className="semibold">{details.value}</span>"</> : ""} will be inserted
                    </>
                );
            case "Section Availability":
                return (
                    <>
                        "<span className="semibold">{details.sectionName}</span>" section in the Selected Form is Available to "<span className="semibold">{details.availability}</span> and fields are Editable: "<span className="semibold">{details.editable ? "True" : "False"}</span>, Mandatory: "<span className="semibold">{details.mandatory ? "True" : "False"}</span>", Visible: "<span className="semibold">{details.visible ? "True" : "False"}</span>"
                    </>
                );

            default:
                return <>{eventType}</>;
        }
    };

    const renderThenCell = (row) => {
        const formattedThen = buildThenStructures(row.Then);
        if (!formattedThen.length) return <span className="text-new-automation" onClick={() => setNewThenAutomation(row.AutomationOrder)}>Set an Automation</span>;

        return (
            <div>
                {formattedThen.map((item, idx) => {
                    const availabilityText = item.availability
                        .map(a => a.groupName || a.userName || a.roleName)
                        .join(", ");

                    const description = formatThenEventType(item.eventType, {
                        ...item,
                        availability: availabilityText
                    });

                    return (
                        <div key={item.id} style={{ marginBottom: "8px" }}>
                            <div className={`${item.condition != null && item.condition !== "" ? "mt-2 mb-2" : ""}`}>
                                <strong>{item.condition}</strong>
                            </div>
                            <div className="whenThenEventDescription">{description}</div>
                            <div className="mt-1">
                                <span className="editAutomation" onClick={() => editThenAutomation(row.AutomationOrder, row.Then[idx], idx)} style={{ cursor: "pointer" }}>Edit</span>
                                {(formattedThen.length > 0 && idx == formattedThen.length - 1)
                                    && <> {" · "}
                                        <span className="editAutomation" onClick={() => setNewThenAutomation(row.AutomationOrder, true)} style={{ cursor: "pointer" }}>
                                            Chain Automation
                                        </span>
                                    </>}
                                {(formattedThen.length == 1 || (formattedThen.length > 1 && idx > 0)) && <span className="deleteAutomation" onClick={() => handleThenDelete(row.AutomationOrder, idx)}>{" · "}Delete</span>}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };
    //#endregion

    //#region Delete Handlers
    const handleWhenDelete = (automationOrder, index) => {
        setRows(prevRows => prevRows.map(row => {
            if (row.AutomationOrder === automationOrder) {
                return {
                    ...row,
                    When: row.When.filter((_, idx) => idx !== index),
                };
            }
            return row;
        }));
    };

    const handleThenDelete = (automationOrder, index) => {
        setRows(prevRows => prevRows.map(row => {
            if (row.AutomationOrder === automationOrder) {
                return {
                    ...row,
                    Then: row.Then.filter((_, idx) => idx !== index),
                };
            }
            return row;
        }));
    };
    //#endregion

    //#region Add/Edit Handlers
    const AddNewAutomation = () => {
        setRows(prevRows => [...prevRows, {
            Id: NIL_UUID,
            AutomationOrder: prevRows.length + 1,
            FormId: selectedForm?.Id,
            TaskTypeId: taskTypeId,
            When: "",
            Then: []
        }]);
        scrollToBottomByClassName("task-type-content-form");
    };

    const removeAutomation = (order) => {
        setRows(prevRows => {
            const filtered = prevRows.filter(r => r.AutomationOrder !== order);
            return filtered.map((r, index) => ({
                ...r,
                AutomationOrder: index + 1
            }));
        });
    };

    const setNewWhenEvent = (order, isChained = false) => {
        setSelectedOrder(order);
        setShowWhenModal({ show: true, isChained, selectedWhenEvent: DEFAULT_WHEN_EVENT_STATE });
    }

    const setNewThenAutomation = (order, isChained = false) => {
        setSelectedOrder(order);
        setShowThenModal({ show: true, isChained, selectedThenEvent: DEFAULT_THEN_EVENT_STATE, selectedThenAvailability: [DEFAULT_DYNAMIC_FORM_AUTOMATION_THEN_AVAILABILITY] });
    }

    const editWhenAutomation = (order, selectedWhenEvent, itemIndex) => {
        setSelectedOrder(order);
        setSelectedWhenIndex(itemIndex);
        setShowWhenModal(
            {
                show: true,
                isChained: selectedWhenEvent?.RelatedToCondition != "" && selectedWhenEvent?.RelatedToCondition != null,
                selectedWhenEvent,
            });
    }

    const editThenAutomation = (order, selectedThenEvent, itemIndex) => {
        setSelectedOrder(order);
        setSelectedThenIndex(itemIndex);
        setShowThenModal(
            {
                show: true,
                isChained: selectedThenEvent?.DynamicFormAutomationThen?.RelatedToCondition != "" && selectedThenEvent?.DynamicFormAutomationThen?.RelatedToCondition != null,
                selectedThenEvent: selectedThenEvent.DynamicFormAutomationThen,
                selectedThenAvailability: selectedThenEvent.DynamicFormAutomationThenAvailability
            });
    }

    const saveWhenEvent = (eventData) => {
        // If editing an existing item, update it
        if (selectedWhenIndex !== null) {
            updateWhenAutomation(selectedOrder, selectedWhenIndex, eventData);
            return;
        }
        setRows(prevRows => prevRows.map(row => {
            if (row.AutomationOrder === selectedOrder) {
                if (showWhenModal.isChained) {
                    return {
                        ...row,
                        When: [...row.When, eventData],
                    };
                } else {
                    return {
                        ...row,
                        When: [eventData],
                    };
                }
            }
            return row;
        }));
        setShowWhenModal({ show: false, isChained: false, selectedWhenEvent: DEFAULT_WHEN_EVENT_STATE });
        setSelectedWhenIndex(null);
    };

    const updateWhenAutomation = (automationOrder, itemIndex, eventData) => {
        setRows(prevRows => prevRows.map(row => {
            if (row.AutomationOrder === automationOrder) {
                const updatedWhen = [...row.When];
                updatedWhen[itemIndex] = eventData;
                return {
                    ...row,
                    When: updatedWhen
                };
            }
            return row;
        }));
        setShowWhenModal({ show: false, isChained: false, selectedWhenEvent: DEFAULT_WHEN_EVENT_STATE });
        setSelectedWhenIndex(null);
    };

    const updateThenAutomation = (automationOrder, itemIndex, eventData, availabilityData) => {
        const updatedThenItem = { DynamicFormAutomationThen: eventData, DynamicFormAutomationThenAvailability: availabilityData };

        setRows(prevRows => prevRows.map(row => {
            if (row.AutomationOrder === automationOrder) {
                const updatedThen = [...row.Then];
                updatedThen[itemIndex] = updatedThenItem;
                return {
                    ...row,
                    Then: updatedThen
                };
            }
            return row;
        }));
        setShowThenModal({ show: false, isChained: false, selectedThenEvent: DEFAULT_THEN_EVENT_STATE, selectedThenAvailability: [DEFAULT_DYNAMIC_FORM_AUTOMATION_THEN_AVAILABILITY] });
        setSelectedThenIndex(null);
    };

    const saveThenAutomation = (eventData, availabilityData) => {
        const newThenItem = { DynamicFormAutomationThen: eventData, DynamicFormAutomationThenAvailability: availabilityData };

        // If editing an existing item, update it
        if (selectedThenIndex !== null) {
            updateThenAutomation(selectedOrder, selectedThenIndex, eventData, availabilityData);
            return;
        }
        if (showThenModal.isChained) {
            setRows(prevRows => prevRows.map(row => {
                if (row.AutomationOrder === selectedOrder) {
                    return {
                        ...row,
                        Then: [...row.Then, newThenItem]
                    };
                }
                return row;
            }));
        }
        else {
            setRows(prevRows => prevRows.map(row => {
                if (row.AutomationOrder === selectedOrder) {
                    return {
                        ...row,
                        Then: [newThenItem]
                    };
                }
                return row;
            }));
        }
        setShowThenModal({ show: false, isChained: false, selectedThenEvent: DEFAULT_THEN_EVENT_STATE, selectedThenAvailability: [DEFAULT_DYNAMIC_FORM_AUTOMATION_THEN_AVAILABILITY] });
        setSelectedThenIndex(null);
    };
    //#endregion

    const OnSave = () => {
        const hasContent = (val) => Array.isArray(val) ? val.length > 0 : !!val;
        const completeRows = rows
            .filter(row => hasContent(row.When) && hasContent(row.Then))
            .map((row, index) => ({
                ...row,
                AutomationOrder: index + 1
            }));
        SetFormAutomation(completeRows, selectedForm?.Id);
        setSelectedForm(null);
    };

    const onCancel = () => {
        setSelectedForm(null);
    }

    const changeOrder = (row, direction) => {        
        let newRows = [...rows];
        let newCurrentRow = newRows.find(
            (f) => f.AutomationOrder === row.AutomationOrder
        );
        try {
            if (direction === "up") {
                //Animation For row swapping, Id's are unique by appending FieldId + Column ID
                let a = document.getElementById(`animate-` + (newCurrentRow.AutomationOrder));
                let b = document.getElementById(`animate-` + (newCurrentRow.AutomationOrder - 1));
                //remove existing classes
                a.classList.remove("moveUp");
                a.classList.remove("moveDown");
                b.classList.remove("moveUp");
                b.classList.remove("moveDown");
                //trigger reflow
                void a.offsetWidth;
                void b.offsetWidth;
                //add animation
                a.classList.add("moveDown");
                b.classList.add("moveUp");
                let secondField = newRows.find(
                    (f) => f.AutomationOrder === newCurrentRow.AutomationOrder - 1
                );
                secondField.AutomationOrder += 1;
                newCurrentRow.AutomationOrder -= 1;
                newRows.sort((a, b) => a.AutomationOrder - b.AutomationOrder);
            }
            else if (direction === "down") {
                let a = document.getElementById(`animate-` + (newCurrentRow.AutomationOrder));
                let b = document.getElementById(`animate-` + (newCurrentRow.AutomationOrder + 1));
                a.classList.remove("moveUp");
                a.classList.remove("moveDown");
                b.classList.remove("moveUp");
                b.classList.remove("moveDown");
                //trigger reflow
                void a.offsetWidth;
                void b.offsetWidth;
                //add animation
                a.classList.add("moveUp");
                b.classList.add("moveDown");
                let secondField = newRows.find(
                    (f) => f.AutomationOrder === newCurrentRow.AutomationOrder + 1
                );
                secondField.AutomationOrder -= 1;
                newCurrentRow.AutomationOrder += 1;
                newRows.sort((a, b) => a.AutomationOrder - b.AutomationOrder);
            }
            setRows(newRows);
        }
        catch {
            console.log("Error in Ordering Animation");
        }
    };

    return (
        <div className="automations-wrapper m-2">
            {error && <div className="text-danger small">Failed to load automations.</div>}

            <div className="automation-header">
                <div className="text-title text-left  ">
                    {selectedForm?.Label}
                    <div className="text-subtitle text-left">{rows.length} Automations</div>
                </div>
                <div>
                    <FluentButton
                        icon={
                            <FluentUiIcon name={"AddRegular"} color={"#0F6CBD"} size={20} />
                        }
                        onClick={() => { AddNewAutomation(); }}
                        text={"New Automation"}
                    />
                </div>

            </div>
            <Table size="sm" className="automations-table compact">
                <thead>
                    <tr>
                        <th style={{ color: "#000", fontWeight: 600, alignContent: "center" }}>#</th>
                        <th style={{ color: "#000", fontWeight: 600, alignContent: "center" }}>
                            <span style={{ display: "inline-block", width: 14, height: 14, borderRadius: "50%", backgroundColor: "#e0a346ff", verticalAlign: "bottom" }}></span>&nbsp;WHEN
                        </th>
                        <th style={{ color: "#000", fontWeight: 600, alignContent: "center" }}>
                            <span style={{ display: "inline-block", width: 14, height: 14, borderRadius: "50%", backgroundColor: "#008610ff", verticalAlign: "bottom" }}></span>&nbsp;THEN
                        </th>
                        <th></th>

                    </tr>
                </thead>
                <tbody>
                    {loading ? <div className="pulse-loader" data-testid="loading-spinner">
                        <PulseLoader></PulseLoader>
                    </div>
                        :
                        rows?.map((r, index) => (
                            <tr key={r.AutomationOrder} id={`animate-` + r.AutomationOrder} className="automation-row">
                                <td className="whenThenEventDescription"><div className="whenThenEventDescription">{r.AutomationOrder}</div></td>
                                <td className="whenThenEventDescription">
                                    {renderWhenCell(r)}
                                </td>
                                <td >{renderThenCell(r)}
                                </td>
                                <td className="automation-actions">
                                    <div className="automation-order-buttons">
                                        {index !== 0 && <div onClick={() => changeOrder(r, "up")} >
                                            <FluentUiIcon
                                                name={"ChevronUpRegular"}
                                                color={"#605E5C"}
                                                size={20}
                                            />
                                        </div>}

                                        {index !== rows.length - 1 && <div onClick={() => changeOrder(r, "down")}>
                                            <FluentUiIcon
                                                name={"ChevronDownRegular"}
                                                color={"#605E5C"}
                                                size={20}
                                            />
                                        </div>}
                                    </div>
                                    <FluentButton
                                        className="btn-delete-automation"
                                        icon={
                                            <FluentUiIcon name={"DeleteRegular"} color={"#BC2F32"} size={20} />
                                        }
                                        onClick={() => removeAutomation(r.AutomationOrder)}
                                    />
                                </td>
                            </tr>
                        ))}
                    {
                        rows.length === 0 && !loading && !error &&
                        <td colSpan={3} className="text-center py-4">
                            <EmptyState />
                        </td>
                    }
                </tbody>
            </Table>
            {!loading && <div className="automations-footer">
                <div className="d-flex header justify-content-end">
                    <div className="mr-2">
                        <FluentButton
                            onClick={onCancel}
                            text={"Cancel"}
                        />
                    </div>
                    <FluentButton
                        icon={
                            <FluentUiIcon
                                name={"SaveRegular"}
                                color={"#212121"}
                                size={20}
                            />
                        }
                        onClick={OnSave}
                        text={"Save Automations"}
                    />
                </div>
            </div>}
            {showWhenModal?.show && <WhenModal
                isOpen={showWhenModal.show}
                isChained={showWhenModal.isChained}
                selectedWhenEvent={showWhenModal.selectedWhenEvent}
                onClose={() => { setShowWhenModal(false); setSelectedOrder(null); setSelectedWhenIndex(null); }}
                onSave={saveWhenEvent}
                taskStatusList={taskStatusList}
                formStatuses={formStatuses}
                fieldsInputs={fieldsInputs}
                allForms={allForms}
                selectedFormId={selectedForm?.Id}
                existingWhenEvents={rows.find(r => r.AutomationOrder === selectedOrder)?.When}
                editingIndex={selectedWhenIndex}
            />}
            {showThenModal?.show && <ThenModal
                isOpen={showThenModal.show}
                isChained={showThenModal.isChained}
                selectedThenEvent={showThenModal.selectedThenEvent}
                selectedThenAvailability={showThenModal.selectedThenAvailability}
                onClose={() => { setShowThenModal({ show: false, isChained: false, selectedThenEvent: DEFAULT_THEN_EVENT_STATE, selectedThenAvailability: [DEFAULT_DYNAMIC_FORM_AUTOMATION_THEN_AVAILABILITY] }); setSelectedOrder(null); setSelectedThenIndex(null); }}
                onSave={saveThenAutomation}
                formStatuses={formStatuses}
                fieldsInputs={fieldsInputs}
                currentForm={selectedForm}
                allUsers={allUsers}
                existingThenEvents={rows.find(r => r.AutomationOrder === selectedOrder)?.Then}
                editingIndex={selectedThenIndex}
            />}
        </div>
    );
};

AutomationsGrid.propTypes = {
    allForms: PropTypes.array,
    selectedForm: PropTypes.shape({
        Id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        Label: PropTypes.string,
        SubForms: PropTypes.array,
    }),
    taskTypeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    taskStatusList: PropTypes.array,
    formStatuses: PropTypes.array,
    allUsers: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    formAutomationFromParent: PropTypes.array,
    SetFormAutomation: PropTypes.func.isRequired,
    setSelectedForm: PropTypes.func.isRequired,
};

export default AutomationsGrid;
